import React from 'react';
import { Button, DatePicker, Input, Spin, Table } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { useM310Report } from './hooks/useM310Report';
import type { M310ReportRow } from '../../services/m310Api';
import './M310Form.css';

const columns: ColumnsType<M310ReportRow> = [
  {
    title: '庫位',
    dataIndex: 'fm030_01',
    key: 'fm030_01',
    width: 80,
    sorter: (a, b) => a.fm030_01.localeCompare(b.fm030_01),
  },
  {
    title: '產品代碼',
    dataIndex: 'fm030_02',
    key: 'fm030_02',
    width: 120,
    sorter: (a, b) => a.fm030_02.localeCompare(b.fm030_02),
  },
  {
    title: '批號',
    dataIndex: 'fm030_03',
    key: 'fm030_03',
    width: 120,
    sorter: (a, b) => a.fm030_03.localeCompare(b.fm030_03),
  },
  {
    title: '倉庫',
    dataIndex: 'fm030_12',
    key: 'fm030_12',
    width: 70,
  },
  {
    title: '入帳日期',
    dataIndex: 'fm030_13',
    key: 'fm030_13',
    width: 110,
    render: (v: string | null) => v ? dayjs(v).format('YYYY-MM-DD') : '-',
    sorter: (a, b) => (a.fm030_13 || '').localeCompare(b.fm030_13 || ''),
  },
  {
    title: '庫存量',
    dataIndex: 'fm030_16',
    key: 'fm030_16',
    width: 100,
    align: 'right',
    render: (v: number) => (
      <span style={{ color: '#f5222d', fontWeight: 600 }}>
        {v.toFixed(2)}
      </span>
    ),
    sorter: (a, b) => a.fm030_16 - b.fm030_16,
  },
  {
    title: '庫存金額',
    dataIndex: 'fm030_17',
    key: 'fm030_17',
    width: 120,
    align: 'right',
    render: (v: number) => (
      <span style={{ color: '#f5222d' }}>
        {v.toFixed(2)}
      </span>
    ),
    sorter: (a, b) => a.fm030_17 - b.fm030_17,
  },
  {
    title: '平均單價',
    dataIndex: 'fm030_18',
    key: 'fm030_18',
    width: 100,
    align: 'right',
    render: (v: number) => v.toFixed(3),
    sorter: (a, b) => a.fm030_18 - b.fm030_18,
  },
  {
    title: '顏色',
    dataIndex: 'fm030_51',
    key: 'fm030_51',
    width: 70,
  },
  {
    title: '等級',
    dataIndex: 'fm030_52',
    key: 'fm030_52',
    width: 60,
  },
];

const M310Form: React.FC = () => {
  const {
    loading, records, total,
    startLoc, setStartLoc,
    endLoc, setEndLoc,
    startProd, setStartProd,
    endProd, setEndProd,
    startBatch, setStartBatch,
    endBatch, setEndBatch,
    startDate, setStartDate,
    endDate, setEndDate,
    handleQuery,
  } = useM310Report();

  return (
    <Spin spinning={loading}>
      <div className="m310-form">
        <h2 style={{ marginBottom: 16 }}>
          庫位批號庫存表(量負) — pwm310f1
          {total > 0 && (
            <span style={{ marginLeft: 12, fontSize: 14, fontWeight: 400, color: '#f5222d' }}>
              共 {total} 筆負庫存
            </span>
          )}
        </h2>

        {/* Filter: Location range */}
        <div className="filter-row">
          <span className="filter-label">庫位代碼：</span>
          <Input
            placeholder="起始"
            value={startLoc}
            onChange={(e) => setStartLoc(e.target.value)}
            style={{ width: 120 }}
          />
          <span className="filter-sep">～</span>
          <Input
            placeholder="結束"
            value={endLoc}
            onChange={(e) => setEndLoc(e.target.value)}
            style={{ width: 120 }}
          />
        </div>

        {/* Filter: Product code range */}
        <div className="filter-row">
          <span className="filter-label">產品代碼：</span>
          <Input
            placeholder="起始"
            value={startProd}
            onChange={(e) => setStartProd(e.target.value)}
            style={{ width: 140 }}
          />
          <span className="filter-sep">～</span>
          <Input
            placeholder="結束"
            value={endProd}
            onChange={(e) => setEndProd(e.target.value)}
            style={{ width: 140 }}
          />
        </div>

        {/* Filter: Batch number range */}
        <div className="filter-row">
          <span className="filter-label">批號：</span>
          <Input
            placeholder="起始"
            value={startBatch}
            onChange={(e) => setStartBatch(e.target.value)}
            style={{ width: 140 }}
          />
          <span className="filter-sep">～</span>
          <Input
            placeholder="結束"
            value={endBatch}
            onChange={(e) => setEndBatch(e.target.value)}
            style={{ width: 140 }}
          />
        </div>

        {/* Filter: Entry date range */}
        <div className="filter-row">
          <span className="filter-label">入帳日期：</span>
          <DatePicker
            placeholder="起始日"
            value={startDate ? dayjs(startDate) : null}
            onChange={(d) => setStartDate(d ? d.format('YYYY-MM-DD') : '')}
            style={{ width: 140 }}
          />
          <span className="filter-sep">～</span>
          <DatePicker
            placeholder="結束日"
            value={endDate ? dayjs(endDate) : null}
            onChange={(d) => setEndDate(d ? d.format('YYYY-MM-DD') : '')}
            style={{ width: 140 }}
          />
        </div>

        {/* Query button */}
        <div className="action-row">
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleQuery}
            loading={loading}
          >
            開始查詢
          </Button>
        </div>

        {/* Results table */}
        <Table<M310ReportRow>
          columns={columns}
          dataSource={records}
          rowKey={(r) => `${r.fm030_01}-${r.fm030_02}-${r.fm030_03}`}
          pagination={{
            pageSize: 100,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 筆`,
          }}
          size="small"
          scroll={{ x: 1000 }}
          locale={{ emptyText: '請設定篩選條件後按「開始查詢」' }}
        />
      </div>
    </Spin>
  );
};

export default M310Form;
